import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Save, Eye, EyeOff, Lock, Mail, Bell, BellOff, ShieldCheck } from 'lucide-react';
import Tooltip from '../components/Tooltip';
import ajuda from '../utils/ajuda';
import {
  pushSuportado, statusPermissao, subscriptionAtual,
  ativarPush, desativarPush, enviarTeste
} from '../services/push';
import { useT } from '../i18n';

function Campo({ label, value, onChange, type = 'text', senhaKey, tooltip, mostrarSenhas = {}, onToggleSenha }) {
  return (
    <div className="field">
      <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {tooltip && ajuda[tooltip] && <Tooltip texto={ajuda[tooltip].texto} exemplo={ajuda[tooltip].exemplo} />}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={senhaKey && !mostrarSenhas[senhaKey] ? 'password' : type}
          value={value} onChange={onChange}
          className="input"
          style={{ paddingRight: senhaKey ? '40px' : undefined }}
        />
        {senhaKey && onToggleSenha && (
          <button type="button" onClick={() => onToggleSenha(senhaKey)} style={{
            position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)',
            padding: 4, borderRadius: 6
          }}>
            {mostrarSenhas[senhaKey] ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
    </div>
  );
}

const SectionTitle = ({ icon, children }) => (
  <p style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
    {icon} {children}
  </p>
);

export default function Configuracoes() {
  const { t } = useT();
  const { usuario, logout, isSuperadmin, addonEmail, tenant, carregarTenant } = useAuth();

  const [whatsapp, setWhatsapp] = useState({ api: '', apikey: '' });
  const [openai, setOpenai] = useState({ key: '' });
  const [smtp, setSmtp] = useState({ host: '', port: '587', user: '', pass: '', from: '' });
  const [mostrarSenhas, setMostrarSenhas] = useState({});
  const [salvando, setSalvando] = useState('');

  const [formSenha, setFormSenha] = useState({ senha_atual: '', nova_senha: '', confirmar: '' });
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  const [formEmail, setFormEmail] = useState({ novo_email: '', senha: '' });
  const [salvandoEmail, setSalvandoEmail] = useState(false);
  const [contratandoAddon, setContratandoAddon] = useState(false);

  const [assinatura, setAssinatura] = useState(null);
  const [solicitandoReembolso, setSolicitandoReembolso] = useState(false);

  // Push notifications
  const [pushAtivo, setPushAtivo] = useState(false);
  const [pushPermissao, setPushPermissao] = useState('default');
  const [pushOcupado, setPushOcupado] = useState(false);
  const pushOk = pushSuportado();

  useEffect(() => {
    api.get('/config').then(r => {
      if (r.data.whatsapp) setWhatsapp(r.data.whatsapp);
      if (r.data.openai) setOpenai(r.data.openai);
      if (r.data.smtp) setSmtp(r.data.smtp);
    }).catch(() => {});

    api.get('/pagamento/minha-assinatura').then(({ data }) => setAssinatura(data)).catch(() => {});

    const params = new URLSearchParams(window.location.search);
    if (params.get('addon') === 'ativado') {
      toast.success(t('configuracoes.toast_addon_ativado'));
      carregarTenant();
      window.history.replaceState({}, '', '/configuracoes');
    } else if (params.get('addon') === 'erro') {
      toast.error(t('configuracoes.toast_addon_erro'));
      window.history.replaceState({}, '', '/configuracoes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pushOk) return;
    setPushPermissao(statusPermissao());
    subscriptionAtual()
      .then(sub => setPushAtivo(!!sub))
      .catch(() => {});
  }, [pushOk]);

  async function handleAtivarPush() {
    setPushOcupado(true);
    try {
      await ativarPush();
      setPushAtivo(true);
      setPushPermissao(statusPermissao());
      toast.success(t('configuracoes.toast_push_ativado'));
    } catch (err) {
      toast.error(err.message || t('configuracoes.toast_push_erro_desativar'));
    } finally {
      setPushOcupado(false);
    }
  }

  async function handleDesativarPush() {
    setPushOcupado(true);
    try {
      await desativarPush();
      setPushAtivo(false);
      toast.success(t('configuracoes.toast_push_desativado'));
    } catch {
      toast.error(t('configuracoes.toast_push_erro_desativar'));
    } finally {
      setPushOcupado(false);
    }
  }

  async function handleTestePush() {
    setPushOcupado(true);
    try {
      await enviarTeste();
      toast.success(t('configuracoes.toast_push_teste'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracoes.toast_push_erro_teste'));
    } finally {
      setPushOcupado(false);
    }
  }

  function toggleSenha(key) {
    setMostrarSenhas(prev => ({ ...prev, [key]: !prev[key] }));
  }

  async function salvar(secao, dados) {
    setSalvando(secao);
    try {
      await api.post('/config', { secao, dados });
      toast.success(t('configuracoes.toast_config_salvas'));
    } catch {
      toast.error(t('configuracoes.toast_erro_salvar'));
    } finally {
      setSalvando('');
    }
  }

  async function handleAlterarSenha() {
    if (!formSenha.senha_atual || !formSenha.nova_senha) {
      toast.error(t('configuracoes.toast_campos_obrigatorios'));
      return;
    }
    if (formSenha.nova_senha !== formSenha.confirmar) {
      toast.error(t('configuracoes.toast_senhas_nao_conferem'));
      return;
    }
    if (formSenha.nova_senha.length < 6) {
      toast.error(t('configuracoes.toast_senha_curta'));
      return;
    }
    setSalvandoSenha(true);
    try {
      await api.post('/config/alterar-senha', {
        senha_atual: formSenha.senha_atual,
        nova_senha: formSenha.nova_senha
      });
      toast.success(t('configuracoes.toast_senha_alterada'));
      setFormSenha({ senha_atual: '', nova_senha: '', confirmar: '' });
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracoes.toast_erro_senha'));
    } finally {
      setSalvandoSenha(false);
    }
  }

  async function handleAlterarEmail() {
    if (!formEmail.novo_email || !formEmail.senha) {
      toast.error(t('configuracoes.toast_campos_obrigatorios'));
      return;
    }
    setSalvandoEmail(true);
    try {
      await api.post('/config/alterar-email', formEmail);
      toast.success(t('configuracoes.toast_email_alterado'));
      setFormEmail({ novo_email: '', senha: '' });
      setTimeout(() => logout(), 2000);
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracoes.toast_erro_email'));
    } finally {
      setSalvandoEmail(false);
    }
  }

  async function handleSolicitarReembolso() {
    if (!window.confirm(t('configuracoes.confirm_reembolso'))) return;
    setSolicitandoReembolso(true);
    try {
      await api.post('/pagamento/solicitar-reembolso');
      toast.success(t('configuracoes.toast_reembolso_enviado'));
      setAssinatura(prev => ({ ...prev, reembolso_solicitado: true }));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracoes.toast_reembolso_erro'));
    } finally {
      setSolicitandoReembolso(false);
    }
  }

  async function handleContratarAddon() {
    setContratandoAddon(true);
    try {
      const { data } = await api.post('/pagamento/addon-checkout', {
        addon: 'email',
        tenant_id: tenant?.id
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracoes.toast_addon_contratar_erro'));
    } finally {
      setContratandoAddon(false);
    }
  }

  const linkSmtp = { color: 'var(--color-accent)', fontSize: '11px', fontWeight: 500 };

  return (
    <div>
      <div className="page-h">
        <div>
          <h1 className="page-h-title">{t('configuracoes.titulo')}</h1>
          <p className="page-h-sub">{t('configuracoes.subtitulo')}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '1rem', maxWidth: '960px' }}>

        {/* Alterar senha */}
        <div className="card">
          <SectionTitle icon={<Lock size={16} color="var(--color-accent)" />}>{t('configuracoes.sec_senha')}</SectionTitle>
          <Campo label={t('configuracoes.label_senha_atual')} value={formSenha.senha_atual}
            onChange={e => setFormSenha({ ...formSenha, senha_atual: e.target.value })}
            senhaKey="senha_atual" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
          <Campo label={t('configuracoes.label_nova_senha')} value={formSenha.nova_senha}
            onChange={e => setFormSenha({ ...formSenha, nova_senha: e.target.value })}
            senhaKey="nova_senha" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
          <Campo label={t('configuracoes.label_confirmar_senha')} value={formSenha.confirmar}
            onChange={e => setFormSenha({ ...formSenha, confirmar: e.target.value })}
            senhaKey="confirmar" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
          <button onClick={handleAlterarSenha} disabled={salvandoSenha} className="btn btn-primary btn-block">
            <Save size={14} /> {salvandoSenha ? t('configuracoes.salvando') : t('configuracoes.btn_alterar_senha')}
          </button>
        </div>

        {/* Alterar email */}
        <div className="card">
          <SectionTitle icon={<Mail size={16} color="var(--color-accent)" />}>{t('configuracoes.sec_email')}</SectionTitle>
          <div className="info-box" style={{ marginBottom: '1rem' }}>
            {t('configuracoes.email_atual_label')} <strong style={{ color: 'var(--color-text)' }}>{usuario?.email}</strong>
          </div>
          <Campo label={t('configuracoes.label_novo_email')} value={formEmail.novo_email}
            onChange={e => setFormEmail({ ...formEmail, novo_email: e.target.value })} type="email"
            mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
          <Campo label={t('configuracoes.label_confirmar_senha_email')} value={formEmail.senha}
            onChange={e => setFormEmail({ ...formEmail, senha: e.target.value })}
            senhaKey="email_senha" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
          <div className="info-box info-box-warn" style={{ marginBottom: '1rem' }}>
            {t('configuracoes.warn_email')}
          </div>
          <button onClick={handleAlterarEmail} disabled={salvandoEmail} className="btn btn-primary btn-block">
            <Save size={14} /> {salvandoEmail ? t('configuracoes.salvando') : t('configuracoes.btn_alterar_email')}
          </button>
        </div>

        {/* Push notifications */}
        <div className="card">
          <SectionTitle icon={<Bell size={16} color="var(--color-accent)" />}>{t('configuracoes.sec_push')}</SectionTitle>
          {!pushOk ? (
            <p className="muted" style={{ fontSize: 13 }}>
              {t('configuracoes.push_sem_suporte')}
            </p>
          ) : pushPermissao === 'denied' ? (
            <div className="info-box info-box-warn">
              {t('configuracoes.push_bloqueado')}
            </div>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14 }}>
                {t('configuracoes.push_descricao')}
              </p>
              {pushAtivo ? (
                <>
                  <div className="info-box info-box-success" style={{ marginBottom: 12 }}>
                    {t('configuracoes.push_ativo')}
                  </div>
                  <div className="row">
                    <button onClick={handleTestePush} disabled={pushOcupado} className="btn btn-secondary" style={{ flex: 1 }}>
                      <Bell size={14} /> {t('configuracoes.btn_teste_push')}
                    </button>
                    <button onClick={handleDesativarPush} disabled={pushOcupado} className="btn btn-warning">
                      <BellOff size={14} /> {t('configuracoes.btn_desativar_push')}
                    </button>
                  </div>
                </>
              ) : (
                <button onClick={handleAtivarPush} disabled={pushOcupado} className="btn btn-primary btn-block">
                  <Bell size={14} /> {pushOcupado ? t('configuracoes.btn_ativando_push') : t('configuracoes.btn_ativar_push')}
                </button>
              )}
            </>
          )}
        </div>

        {/* WhatsApp — apenas superadmin */}
        {isSuperadmin && (
          <div className="card">
            <SectionTitle>{t('configuracoes.sec_whatsapp')}</SectionTitle>
            <Campo label={t('configuracoes.label_url_api')} value={whatsapp.api}
              onChange={e => setWhatsapp({ ...whatsapp, api: e.target.value })}
              tooltip="whatsapp_apikey" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
            <Campo label={t('configuracoes.label_api_key')} value={whatsapp.apikey}
              onChange={e => setWhatsapp({ ...whatsapp, apikey: e.target.value })}
              senhaKey="apikey" tooltip="whatsapp_apikey" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
            <button onClick={() => salvar('whatsapp', whatsapp)} disabled={salvando === 'whatsapp'} className="btn btn-primary btn-block">
              <Save size={14} /> {salvando === 'whatsapp' ? t('configuracoes.salvando') : t('configuracoes.btn_salvar')}
            </button>
          </div>
        )}

        {/* OpenAI — apenas superadmin */}
        {isSuperadmin && (
          <div className="card">
            <SectionTitle>{t('configuracoes.sec_openai')}</SectionTitle>
            <Campo label={t('configuracoes.label_api_key')} value={openai.key}
              onChange={e => setOpenai({ key: e.target.value })}
              senhaKey="openai" tooltip="openai_key" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
            <div className="info-box info-box-warn" style={{ marginBottom: '1rem' }}>
              {t('configuracoes.openai_warn')}
            </div>
            <button onClick={() => salvar('openai', openai)} disabled={salvando === 'openai'} className="btn btn-primary btn-block">
              <Save size={14} /> {salvando === 'openai' ? t('configuracoes.salvando') : t('configuracoes.btn_salvar')}
            </button>
          </div>
        )}

        {/* Garantia de 7 dias — apenas contas pagas dentro do prazo */}
        {assinatura && assinatura.periodo_cobranca !== 'teste' && assinatura.status === 'ativo' &&
         assinatura.dias_desde_ativacao !== null && assinatura.dias_desde_ativacao <= 7 && (
          <div className="card" style={{ gridColumn: '1 / -1', borderColor: assinatura.reembolso_solicitado ? 'var(--color-border)' : 'rgba(220,38,38,0.25)' }}>
            <SectionTitle icon={<ShieldCheck size={16} color={assinatura.reembolso_solicitado ? 'var(--color-text-muted)' : 'var(--color-danger)'} />}>
              {t('configuracoes.sec_garantia')}
            </SectionTitle>
            {assinatura.reembolso_solicitado ? (
              <div className="info-box info-box-warn">
                {t('configuracoes.garantia_reembolso_solicitado')}
              </div>
            ) : (
              <>
                <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14 }}>
                  {t('configuracoes.garantia_texto')}
                  <br />
                  <strong style={{ color: 'var(--color-text)' }}>
                    {t('configuracoes.garantia_prazo', { dias: 7 - assinatura.dias_desde_ativacao })}
                  </strong>
                </p>
                <button
                  onClick={handleSolicitarReembolso}
                  disabled={solicitandoReembolso}
                  className="btn"
                  style={{ background: 'var(--color-danger)', color: '#fff', borderColor: 'transparent' }}
                >
                  {solicitandoReembolso ? t('configuracoes.btn_enviando') : t('configuracoes.btn_solicitar_reembolso')}
                </button>
              </>
            )}
          </div>
        )}

        {/* SMTP — condicionado ao addon */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <SectionTitle>{t('configuracoes.sec_smtp')}</SectionTitle>

          {addonEmail || isSuperadmin ? (
            <>
              <p style={{ fontSize: '12px', color: 'var(--color-success)', marginBottom: '0.5rem', fontWeight: 600 }}>
                {t('configuracoes.smtp_ativo')}
              </p>
              <div className="info-box" style={{ marginBottom: '1rem', lineHeight: 1.7 }}>
                {t('configuracoes.smtp_where')}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '6px' }}>
                  <a href="https://support.google.com/a/answer/176600" target="_blank" rel="noreferrer" style={linkSmtp}>Gmail →</a>
                  <a href="https://support.microsoft.com/pt-br/office/configura%C3%A7%C3%B5es-de-email-pop-imap-e-smtp-8361e398-8af4-4e97-b147-6c6c4ac95353" target="_blank" rel="noreferrer" style={linkSmtp}>Outlook →</a>
                  <a href="https://support.hostinger.com/pt/articles/1583langpt-configura%C3%A7%C3%B5es-smtp" target="_blank" rel="noreferrer" style={linkSmtp}>Hostinger →</a>
                  <a href="https://www.locaweb.com.br/ajuda/wiki/configuracoes-de-email/" target="_blank" rel="noreferrer" style={linkSmtp}>Locaweb →</a>
                  <a href="https://suporte.kinghost.com.br/base-de-conhecimento/como-configurar-o-email/" target="_blank" rel="noreferrer" style={linkSmtp}>KingHost →</a>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <Campo label={t('configuracoes.label_smtp_host')} value={smtp.host}
                  onChange={e => setSmtp({ ...smtp, host: e.target.value })}
                  tooltip="smtp_host" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
                <Campo label={t('configuracoes.label_smtp_porta')} value={smtp.port}
                  onChange={e => setSmtp({ ...smtp, port: e.target.value })}
                  tooltip="smtp_port" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
                <Campo label={t('configuracoes.label_smtp_from')} value={smtp.from}
                  onChange={e => setSmtp({ ...smtp, from: e.target.value })}
                  tooltip="smtp_from" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
                <Campo label={t('configuracoes.label_smtp_user')} value={smtp.user}
                  onChange={e => setSmtp({ ...smtp, user: e.target.value })}
                  tooltip="smtp_user" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
                <Campo label={t('configuracoes.label_smtp_pass')} value={smtp.pass}
                  onChange={e => setSmtp({ ...smtp, pass: e.target.value })}
                  senhaKey="smtp" tooltip="smtp_pass" mostrarSenhas={mostrarSenhas} onToggleSenha={toggleSenha} />
              </div>
              <button onClick={() => salvar('smtp', smtp)} disabled={salvando === 'smtp'} className="btn btn-primary" style={{ marginTop: '0.5rem' }}>
                <Save size={14} /> {salvando === 'smtp' ? t('configuracoes.salvando') : t('configuracoes.btn_salvar_smtp')}
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <p style={{ fontSize: '15px', color: 'var(--color-text)', marginBottom: '8px', fontWeight: 600 }}>
                {t('configuracoes.addon_titulo')}
              </p>
              <p className="muted" style={{ fontSize: '13px', marginBottom: '1.5rem' }}>
                {t('configuracoes.addon_descricao')}
              </p>
              <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '4px', marginBottom: '1rem' }}>
                <span style={{ fontSize: '32px', fontWeight: 700, color: 'var(--color-text)', letterSpacing: '-0.02em' }}>+R$ 10</span>
                <span className="muted" style={{ fontSize: '14px' }}>{t('configuracoes.addon_periodo')}</span>
              </div>
              <br />
              <button onClick={handleContratarAddon} disabled={contratandoAddon} className="btn btn-lg"
                style={{
                  background: 'linear-gradient(135deg, #16a34a 0%, #13305f 100%)',
                  color: '#fff', borderColor: 'transparent',
                  boxShadow: '0 10px 24px rgba(22, 163, 74,0.30)'
                }}>
                {contratandoAddon ? t('configuracoes.btn_processando') : t('configuracoes.btn_contratar_addon')}
              </button>
              <p className="muted" style={{ fontSize: '11px', marginTop: '12px' }}>
                {t('configuracoes.addon_rodape')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
